import { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { shortLabel } from "./chart-utils";
import type { ChartLocation } from "./useChartData";

interface CategoryHeatmapProps {
  locations: ChartLocation[];
}

/** Interpolate the brand score scale: coral (0) → amber (50) → mint (100).
   Endpoints match --score-low #c75c5c, --score-med #c97d2d, --score-high #2e9f70. */
function heatColor(score: number): string {
  const s = Math.max(0, Math.min(100, score));
  if (s <= 50) {
    const t = s / 50;
    const r = Math.round(199 + (201 - 199) * t);
    const g = Math.round(92 + (125 - 92) * t);
    const b = Math.round(92 + (45 - 92) * t);
    return `rgb(${r}, ${g}, ${b})`;
  }
  const t = (s - 50) / 50;
  const r = Math.round(201 + (46 - 201) * t);
  const g = Math.round(125 + (159 - 125) * t);
  const b = Math.round(45 + (112 - 45) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

function textColor(score: number): string {
  return score < 40 ? "white" : "rgb(23, 23, 23)";
}

interface CityRow {
  city: string;
  count: number;
  categories: Record<string, number>;
}

function buildCityRows(locations: ChartLocation[], categoryNames: string[]): CityRow[] {
  const cityMap = new Map<
    string,
    { count: number; catSums: Record<string, { sum: number; n: number }> }
  >();

  for (const loc of locations) {
    const city = loc.addressRaw?.split(",")[1]?.trim();
    if (!city) continue;
    if (!cityMap.has(city)) {
      const catSums: Record<string, { sum: number; n: number }> = {};
      for (const cat of categoryNames) catSums[cat] = { sum: 0, n: 0 };
      cityMap.set(city, { count: 0, catSums });
    }
    const entry = cityMap.get(city)!;
    entry.count += 1;
    for (const cs of loc.categoryScores) {
      const catEntry = entry.catSums[cs.categoryName];
      if (catEntry) {
        catEntry.sum += cs.score;
        catEntry.n += 1;
      }
    }
  }

  return Array.from(cityMap.entries())
    .map(([city, { count, catSums }]) => {
      const categories: Record<string, number> = {};
      for (const cat of categoryNames) {
        const catEntry = catSums[cat];
        categories[cat] = catEntry && catEntry.n > 0 ? Math.round(catEntry.sum / catEntry.n) : 0;
      }
      return { city, count, categories };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

export function CategoryHeatmap({ locations }: CategoryHeatmapProps) {
  const { categoryNames, rows } = useMemo(() => {
    if (locations.length === 0) return { categoryNames: [], rows: [] };
    const names = (locations[0]?.categoryScores ?? []).map((cs) => cs.categoryName);
    return { categoryNames: names, rows: buildCityRows(locations, names) };
  }, [locations]);

  if (rows.length < 2) return null;

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr>
              <th className="text-left px-2 py-1.5 text-xs text-muted-foreground font-medium">
                City
              </th>
              {categoryNames.map((cat) => (
                <Tooltip key={cat}>
                  <TooltipTrigger asChild>
                    <th className="text-center px-1.5 py-1.5 text-xs text-muted-foreground font-medium cursor-default">
                      {shortLabel(cat)}
                    </th>
                  </TooltipTrigger>
                  <TooltipContent>{cat}</TooltipContent>
                </Tooltip>
              ))}
              <th className="text-center px-2 py-1.5 text-xs text-muted-foreground font-medium">
                #
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.city}>
                <td className="px-2 py-1.5 font-medium truncate max-w-[140px]" title={row.city}>
                  {row.city}
                </td>
                {categoryNames.map((cat) => {
                  const score = row.categories[cat] ?? 0;
                  return (
                    <td
                      key={cat}
                      className="text-center px-1.5 py-1.5 text-xs font-medium rounded"
                      style={{
                        backgroundColor: heatColor(score),
                        color: textColor(score),
                      }}
                    >
                      {score}
                    </td>
                  );
                })}
                <td className="text-center px-2 py-1.5 text-xs text-muted-foreground">
                  {row.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}
